import { useEffect, useMemo, useState } from 'react'

import type { AvailabilityBlock } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleAvailability'
import { getEmrLinkedResidenUserIds } from 'features/Patients/state/api/emrProviders.transform'
import { useFetchEmrLinkedProvidersQuery } from 'features/Patients/state/api/patientApi'
import { useFetchScheduleDayQuery } from 'features/Schedule/api/schedule.api'
import { scheduleGridQueryOptions } from 'features/Schedule/api/scheduleQueryOptions'

import { awayStaffIdsForVisit, doctorAwayKey } from './doctorAway'
import { doctorAvailabilityBlocks } from './previewAvailability'

type Visit = {
  staffId: number | undefined
  day: string
  time: Date | string | undefined
  duration: number
  // Every doctor of a joint visit; the back end moves them all together.
  jointStaffIds?: number[]
}

type Args = {
  open: boolean
  visit: Visit
  siteId: string | undefined
  // The visit the user already agreed to book while away, set on each opening.
  acceptedVisit: Visit | null
}

type DoctorAwayCheck = {
  isConfirmOpen: boolean
  // True when Save must wait for the "Doctor is away" answer; opens the prompt.
  requestConfirm: () => boolean
  // Remembers this visit as agreed, then resumes the save.
  accept: (resume: () => void) => void
  cancel: () => void
  // The doctor's schedule for the day preview; empty while loading or when not EMR-linked.
  availabilityBlocks: AvailabilityBlock[]
  // The doctors of the visit who are away at its time.
  awayStaffIds: number[]
}

// Reads the same /schedule/day the calendar uses, for whatever day the form is on.
export const useDoctorAwayCheck = ({
  open,
  siteId,
  visit,
  acceptedVisit,
}: Args): DoctorAwayCheck => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [acceptedKey, setAcceptedKey] = useState<string | null>(null)

  const { currentData: scheduleDay } = useFetchScheduleDayQuery(
    { siteId: siteId ?? '', date: visit.day },
    { skip: !open || !siteId || !visit.day, ...scheduleGridQueryOptions },
  )
  const { data: emrProviders, isSuccess } = useFetchEmrLinkedProvidersQuery(undefined, {
    skip: !open,
  })

  const emrLinkedUserIds = useMemo(
    () => (isSuccess ? getEmrLinkedResidenUserIds(emrProviders ?? []) : null),
    [emrProviders, isSuccess],
  )

  const availability = scheduleDay?.availability
  const availabilityBlocks = useMemo(
    () => doctorAvailabilityBlocks({ availability, emrLinkedUserIds, staffId: visit.staffId }),
    [availability, emrLinkedUserIds, visit.staffId],
  )

  const seedKey = acceptedVisit ? doctorAwayKey(acceptedVisit) : null
  useEffect(() => {
    setIsConfirmOpen(false)
    setAcceptedKey(seedKey)
  }, [open, seedKey])

  const key = doctorAwayKey(visit)
  const staffIds = visit.jointStaffIds?.length
    ? visit.jointStaffIds
    : [visit.staffId].filter((id): id is number => Boolean(id))
  const awayStaffIds = awayStaffIdsForVisit({
    availability: scheduleDay?.availability,
    emrLinkedUserIds,
    staffIds,
    time: visit.time,
    duration: visit.duration,
  })
  const isAway = awayStaffIds.length > 0

  const requestConfirm = () => {
    if (!isAway || key === acceptedKey) return false
    setIsConfirmOpen(true)
    return true
  }

  const accept = (resume: () => void) => {
    setAcceptedKey(key)
    setIsConfirmOpen(false)
    resume()
  }

  return {
    isConfirmOpen,
    requestConfirm,
    accept,
    cancel: () => setIsConfirmOpen(false),
    availabilityBlocks,
    awayStaffIds,
  }
}
